import React from "react";
import { useTranslation } from "react-i18next";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Checkbox,
  Typography,
  Box,
  Switch,
  useTheme,
} from "@mui/material";
import { Circle, Check } from "@phosphor-icons/react";

import { Task } from "api/types/users";
import EmptyTaskList from "./EmptyTaskList";
import useGetClientFormsForSignature from "../hooks/useGetClientFormsForSignature";
import { getTaskDisplayName } from "../utils/getTaskDisplayName";

interface TaskChecklistProps {
  tasks: Task[];
  onTaskToggle?: (task: Task, completed: boolean) => void;
  clientId?: string;
}

const TaskChecklist: React.FC<TaskChecklistProps> = ({ tasks, onTaskToggle = () => {}, clientId }) => {
  const { t } = useTranslation(["dashboard"]);
  const [showCompleted, setShowCompleted] = React.useState(false);
  const theme = useTheme();
  const { grey } = theme.palette;

  const { forms } = useGetClientFormsForSignature(clientId || "", !!clientId);

  const formTypes = forms.map((form) => form.type);

  const handleCheckboxChange = (task: Task, completed: boolean) => {
    if (onTaskToggle) {
      onTaskToggle(task, completed);
    }
  };

  const handleShowCompletedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setShowCompleted(event.target.checked);
  };

  const uncompletedTasks = tasks.filter((task) => {
    if (task.completed) return false;

    const taskType = task?.metadata?.formType || task?.metadata?.documentType;
    if (!taskType) return true;

    return !formTypes.includes(taskType);
  });

  const completedTasks = tasks.filter((task) => {
    if (task.completed) return true;

    const taskType = task?.metadata?.formType || task?.metadata?.documentType;
    if (!taskType) return false;

    return formTypes.includes(taskType);
  });

  const renderTaskList = (taskList: Task[], showCompleted: boolean = false) => {
    if (taskList.length === 0) return null;

    return (
      <Box sx={{ mb: showCompleted ? 0 : 3 }}>
        <List sx={{ py: 0 }}>
          {taskList.map((task) => (
            <ListItem
              key={task.id}
              sx={{
                px: 0,
                cursor: showCompleted ? "default" : "pointer",
                "&:hover": {
                  backgroundColor: showCompleted ? "transparent" : "action.hover",
                },
              }}
              onClick={
                showCompleted ? undefined : () => handleCheckboxChange(task, !task.completed)
              }
            >
              <ListItemIcon sx={{ minWidth: 40 }}>
                <Checkbox
                  checked={showCompleted}
                  disabled={true}
                  size="medium"
                  icon={<Circle size={24} color={grey[600]} weight="regular" />}
                  checkedIcon={<Check size={24} color={grey[600]} weight="regular" />}
                />
              </ListItemIcon>
              <ListItemText
                primary={
                  <Typography
                    variant="body1"
                    component="span"
                    sx={{
                      textDecoration: showCompleted ? "line-through" : "none",
                      color: showCompleted ? "text.disabled" : "text.primary",
                    }}
                  >
                    {getTaskDisplayName(task, t)}
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  if (tasks.length === 0) {
    return <EmptyTaskList />;
  }

  return (
    <Box>
      {renderTaskList(uncompletedTasks, false)}

      {completedTasks.length > 0 && (
        <Box sx={{ mt: 3 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="body1" fontSize={14} color="text.secondary">
              {t("dashboard:parent.myTasks.showCompleted")}
            </Typography>
            <Switch checked={showCompleted} color="primary" onChange={handleShowCompletedChange} />
          </Box>
          {showCompleted && renderTaskList(completedTasks, true)}
        </Box>
      )}
    </Box>
  );
};

export default TaskChecklist;
